import { useEffect } from 'react'

import { ApolloError, useMutation, useQuery } from '@apollo/client'
import { useNotifications } from '@joor/design-system'
import { omit } from 'lodash'
import { DropzoneState, FileRejection, useDropzone } from 'react-dropzone'

import {
  BulkRemoveUnmatchedUploadsDocument,
  GetManageImagesStyleDataPayload,
  SaveProductImagesDocument,
  SaveUnmatchedUploadDocument,
  UnmatchedUpload,
} from '__generated__/atlas-types'

import { logMutationError } from 'utils/sdks/datadog'
import { relayConnectionToArray } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'

import {
  CONSOLE_LOG_KEY,
  GALLERY_PAGE_SIZE,
  UPLOAD_ACCEPTED_FORMAT,
  UPLOAD_GALLERY_MAX_AMOUNT,
  UPLOAD_MAX_SIZE,
} from './manageImagesModal.constants'
import messages, { ERROR_DESCRIPTIONS } from './manageImagesModal.messages'
import {
  NavigationProductsQuery,
  StyleDataQuery,
  UnmatchedUploadsQuery,
} from './manageImagesModal.queries'
import {
  FILE_ERROR_CODES,
  ProductImage,
  Style,
  StyleColor,
} from './manageImagesModal.types'
import { checkIfProductImagesHasChanges } from './manageImagesModals.utils'
import useStore from './store/useManageImagesStore'

const mapStyleData = (style: GetManageImagesStyleDataPayload) => {
  const productImages =
    style?.images?.reduce((acc, image) => {
      if (image) acc.push(omit(image, 'orderNbr'))
      return acc
    }, [] as ProductImage[]) || []

  const styleColors =
    style?.styleColors?.reduce((acc, styleColor) => {
      if (styleColor) acc.push(styleColor)
      return acc
    }, [] as StyleColor[]) || []

  return { productImages, styleColors }
}

export const useStyleData = (
  style: Style | null,
  setStyle: React.Dispatch<React.SetStateAction<Style | null>>,
) => {
  const linesheetId = useStore((state) => state.linesheetId)
  const productId = useStore((state) => state.productId)
  const setProductImages = useStore((state) => state.setProductImages)
  const setProductImagesBak = useStore((state) => state.setProductImagesBak)
  const setStyleColors = useStore((state) => state.setStyleColors)
  const setIsStyleDataLoading = useStore((state) => state.setIsStyleDataLoading)
  const { addToasts } = useNotifications()

  const { loading, data, error } = useQuery(StyleDataQuery, {
    client: atlasClient,
    variables: {
      styleId: productId,
      collectionId: linesheetId,
    },
    skip: !linesheetId || !productId,
  })

  useEffect(() => {
    if (!linesheetId || !productId || loading) {
      // The first render when the IDs are not yet set we return loading true
      setIsStyleDataLoading({ isStyleDataLoading: true })
      return
    }

    if (error || !data || !data.getManageImagesStyleData) {
      const errorMessage = error?.message || 'No Style Data'
      console.error(
        CONSOLE_LOG_KEY + '[Query][StyleData] Error: ' + errorMessage,
      )
      addToasts([
        {
          title: messages.error,
          description: messages.errorLoadingStyle,
          type: 'error',
          shouldCloseManually: true,
        },
      ])
      setIsStyleDataLoading({ isStyleDataLoading: false })
      return
    }

    const styleDataAsString = JSON.stringify(data.getManageImagesStyleData)
    const currentStyleAsString = JSON.stringify(style)
    // We need to do a deep compare to avoid unnecessary updates of the Style data
    if (styleDataAsString === currentStyleAsString) {
      return
    }

    const { productImages, styleColors } = mapStyleData(
      data.getManageImagesStyleData,
    )
    setProductImages({ productImages })
    setProductImagesBak({ productImagesBak: productImages })
    setStyleColors({ styleColors })
    setStyle(data.getManageImagesStyleData as Style)
    setIsStyleDataLoading({ isStyleDataLoading: false })

    return
  }, [
    linesheetId,
    productId,
    loading,
    error,
    data,
    style,
    setProductImages,
    setProductImagesBak,
    setStyleColors,
    setStyle,
    setIsStyleDataLoading,
    addToasts,
  ])
}

export const useUnmatchedUploads = () => {
  const linesheetId = useStore((state) => state.linesheetId)
  const search = useStore((state) => state.search)
  const currentPage = useStore((state) => state.currentPage)
  const setLastPage = useStore((state) => state.setLastPage)

  const { data, loading } = useQuery(UnmatchedUploadsQuery, {
    client: atlasClient,
    variables: {
      collectionId: linesheetId,
      searchFileName: search.trim(),
      limit: GALLERY_PAGE_SIZE,
      offset: (currentPage - 1) * GALLERY_PAGE_SIZE,
    },
    onError: (error: ApolloError) => {
      console.error(
        CONSOLE_LOG_KEY + '[Query][UnmatchedUploads] Error: ' + error.message,
      )
    },
  })

  useEffect(() => {
    if (!data || !data.listUnmatchedUploads) {
      return
    }
    setLastPage({
      lastPage: Math.ceil(
        data.listUnmatchedUploads.totalCount / GALLERY_PAGE_SIZE,
      ),
    })
  }, [data, setLastPage])

  return {
    images: data?.listUnmatchedUploads?.uploads as UnmatchedUpload[] | null,
    loading,
  }
}

export const useBulkRemoveUploads = () => {
  const selectedImages = useStore((state) => state.selectedImages)
  const { addToasts } = useNotifications()
  const [mutate] = useMutation(BulkRemoveUnmatchedUploadsDocument, {
    client: atlasClient,
  })

  const bulkRemoveUploads = async () => {
    const variables = { unmatchedImageIds: Array.from(selectedImages.keys()) }

    try {
      const response = await mutate({ variables })

      if (response.data?.bulkRemoveUnmatchedUploads?.errorCode) {
        throw new Error(response.data.bulkRemoveUnmatchedUploads.errorCode)
      }
      return { error: false }
    } catch (error) {
      addToasts([
        {
          title: messages.errorRemovingImagesTitle,
          description: messages.errorRemovingImagesDesc,
          type: 'error',
          shouldCloseManually: true,
        },
      ])

      logMutationError(
        'BulkRemoveUnmatchedUploads',
        variables,
        `${CONSOLE_LOG_KEY}[Mutation] Error: ${(error as Error).message}`,
      )
      return { error: true }
    }
  }

  return { bulkRemoveUploads }
}

export const useSaveUnmatchedUploads = () => {
  const linesheetId = useStore((state) => state.linesheetId)
  const [mutate] = useMutation(SaveUnmatchedUploadDocument, {
    client: atlasClient,
  })

  const saveUnmatchedUpload = async (
    file: File,
  ): Promise<Partial<{ error: boolean } & UnmatchedUpload>> => {
    try {
      const response = await mutate({
        variables: { collectionId: linesheetId, file, fileName: file.name },
      })

      if (
        response.errors ||
        !response.data ||
        response.data.saveUnmatchedUpload.errorCode
      ) {
        throw Error()
      }

      return {
        error: false,
        id: response.data.saveUnmatchedUpload.uploadId,
        ...response.data.saveUnmatchedUpload,
      }
    } catch (error) {
      return { error: true }
    }
  }

  return { saveUnmatchedUpload }
}

export const useGetDropzoneState = (
  onSuccess: ((image: UnmatchedUpload) => void) | undefined,
  maxFiles: number = UPLOAD_GALLERY_MAX_AMOUNT,
  disabled: boolean = false,
): DropzoneState => {
  const setCurrentPage = useStore((state) => state.setCurrentPage)
  const setIsUploadingImages = useStore((state) => state.setIsUploadingImages)
  const setUploadProgress = useStore((state) => state.setUploadProgress)
  const { saveUnmatchedUpload } = useSaveUnmatchedUploads()
  const { addToasts } = useNotifications()

  const addUploadErrorToast = (
    fileName?: string,
    description: string = ERROR_DESCRIPTIONS['UNKNOWN'],
  ): void => {
    return addToasts([
      {
        title: messages.uploadError + (fileName ? `: ${fileName}` : ''),
        description: description,
        type: 'error',
        shouldCloseManually: true,
      },
    ])
  }

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ): Promise<void> => {
    if (acceptedFiles.length + fileRejections.length > maxFiles) {
      return addUploadErrorToast(
        undefined,
        ERROR_DESCRIPTIONS[FILE_ERROR_CODES.TOO_MANY].replace(
          '{max}',
          maxFiles.toString(),
        ),
      )
    }

    fileRejections.forEach((rejection) => {
      return addUploadErrorToast(
        rejection.file.name,
        ERROR_DESCRIPTIONS[rejection.errors[0].code as FILE_ERROR_CODES],
      )
    })

    if (acceptedFiles.length < 1) {
      return
    }

    setIsUploadingImages({ isUploadingImages: true })

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i]
      const { error, ...imageData } = await saveUnmatchedUpload(file)
      setUploadProgress({
        uploadProgress: ((i + 1) / acceptedFiles.length) * 100,
      })

      if (error) {
        addUploadErrorToast(file.name)
      } else {
        onSuccess && onSuccess(imageData)
      }
    }

    setCurrentPage({ currentPage: 1 })
    atlasClient.cache.evict({ fieldName: 'listUnmatchedUploads' })
    setIsUploadingImages({ isUploadingImages: false })
    setUploadProgress({ uploadProgress: 0 })
  }

  return useDropzone({
    accept: UPLOAD_ACCEPTED_FORMAT,
    disabled,
    maxSize: UPLOAD_MAX_SIZE,
    maxFiles,
    multiple: maxFiles > 1,
    noDrag: true,
    onDrop,
  })
}

export const useSaveProductImages = () => {
  const linesheetId = useStore((state) => state.linesheetId)
  const productId = useStore((state) => state.productId)
  const productImages = useStore((state) => state.productImages)
  const productImagesBak = useStore((state) => state.productImagesBak)
  const styleColors = useStore((state) => state.styleColors)
  const hasStyleColorChanges = useStore((state) => state.hasStyleColorChanges)

  const { addToasts } = useNotifications()
  const [mutate] = useMutation(SaveProductImagesDocument, {
    client: atlasClient,
  })

  const saveProductImages = async () => {
    const hasProductImagesChanges = checkIfProductImagesHasChanges(
      productImagesBak,
      productImages,
    )
    const seen = new Set()
    const mappedProductImages = !hasProductImagesChanges
      ? null
      : productImages
          .map((img, index) => ({
            uploadId: img.id as string,
            unmatchedImageId: img.unmatchedUploadId,
            orderNbr: index + 1,
          }))
          .filter((img) => {
            const realId = img.unmatchedImageId || img.uploadId
            const duplicate = seen.has(realId)
            seen.add(realId)
            return !duplicate
          })
    const mappedStyleColors = !hasStyleColorChanges
      ? null
      : styleColors.map((color) => ({
          styleColorId: color.id as string,
          uploadId: color.imageId,
          unmatchedImageId: color.unmatchedUploadId,
        }))

    const variables = {
      productId,
      collectionId: linesheetId,
      hasProductImagesChanges,
      productImages: mappedProductImages,
      styleColorsImage: mappedStyleColors,
    }
    try {
      const response = await mutate({ variables })

      if (
        response.errors ||
        !response.data ||
        response.data?.saveProductImages?.errorCode
      ) {
        throw response?.errors?.length
          ? response.errors[0]
          : new Error(response.data?.saveProductImages?.errorCode as string)
      }

      addToasts([
        {
          title: messages.success,
          description: messages.successSavingImages,
          type: 'success',
          shouldCloseManually: true,
        },
      ])

      return { error: false }
    } catch (error) {
      addToasts([
        {
          title: messages.error,
          description: messages.errorSavingImages,
          type: 'error',
          shouldCloseManually: true,
        },
      ])

      logMutationError(
        'SaveProductImages',
        variables,
        `${CONSOLE_LOG_KEY}[Mutation] Error: ${(error as Error).message}`,
      )
      return { error: true }
    }
  }

  return { saveProductImages }
}

export const useNavigationProducts = () => {
  const linesheetId = useStore((state) => state.linesheetId)

  const { loading, data } = useQuery(NavigationProductsQuery, {
    client: atlasClient,
    variables: {
      filter: { collectionIds: [linesheetId] },
      orderBy: {
        field: {
          order: 'DESCENDING',
          orderByField: 'MODIFIED',
        },
      },
    },
  })

  const products = relayConnectionToArray(data?.products).map(
    (product: { id: string }) => product.id,
  ) as string[]

  return {
    loading,
    products,
  }
}
